import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";

// Page order for navigation
const PAGE_ORDER = [
  { path: "/", key: "home" },
  { path: "/what-are-peptides", key: "whatArePeptides" },
  { path: "/learn", key: "learnPeptides" },
  { path: "/reconstitution", key: "reconstitution" },
  { path: "/calculator", key: "calculator" },
  { path: "/schedule", key: "schedule" },
  { path: "/get-started", key: "getStarted" },
];

// Scroll to top function
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'instant' });
};

// Navigation link that scrolls to top
const NavLink = ({ to, children, className }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    scrollToTop();
    navigate(to);
  };
  
  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

export const PageNavigation = ({ currentPath }) => {
  const { t } = useTranslation();
  const currentIndex = PAGE_ORDER.findIndex((p) => p.path === currentPath);
  const prevPage = currentIndex > 0 ? PAGE_ORDER[currentIndex - 1] : null;
  const nextPage = currentIndex < PAGE_ORDER.length - 1 ? PAGE_ORDER[currentIndex + 1] : null;

  return (
    <div className="w-full border-t border-border/50 bg-muted/10 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          {/* Previous Page */}
          {prevPage ? (
            <NavLink to={prevPage.path}>
              <Button
                variant="outline"
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">{t(`nav.${prevPage.key}`)}</span>
                <span className="sm:hidden">{t('common.back')}</span>
              </Button>
            </NavLink>
          ) : (
            <div />
          )}

          {/* Next Page */}
          {nextPage ? (
            <NavLink to={nextPage.path}>
              <Button className="gap-2">
                <span className="hidden sm:inline">{t('common.next')}: {t(`nav.${nextPage.key}`)}</span>
                <span className="sm:hidden">{t('common.next')}</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </NavLink>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
};

// Elegant top navigation breadcrumb style
export const PageBreadcrumb = ({ currentPath }) => {
  const { t } = useTranslation();
  const currentIndex = PAGE_ORDER.findIndex((p) => p.path === currentPath);
  const prevPage = currentIndex > 0 ? PAGE_ORDER[currentIndex - 1] : null;
  const nextPage = currentIndex < PAGE_ORDER.length - 1 ? PAGE_ORDER[currentIndex + 1] : null;

  // Don't show on home page
  if (currentPath === "/") return null;

  return (
    <div className="w-full bg-muted/20 border-b border-border/30 py-2 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between text-sm">
          {/* Previous link */}
          {prevPage ? (
            <NavLink
              to={prevPage.path}
              className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{t(`nav.${prevPage.key}`)}</span>
            </NavLink>
          ) : (
            <div />
          )}

          {/* Current page indicator */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="hidden sm:inline text-xs uppercase tracking-wider">
              {currentIndex + 1} {t('common.of')} {PAGE_ORDER.length}
            </span>
            <div className="flex gap-1">
              {PAGE_ORDER.map((page, i) => (
                <NavLink key={page.path} to={page.path}>
                  <div
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === currentIndex
                        ? "bg-primary"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    }`}
                  />
                </NavLink>
              ))}
            </div>
          </div>

          {/* Next link */}
          {nextPage ? (
            <NavLink
              to={nextPage.path}
              className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
            >
              <span className="hidden sm:inline">{t(`nav.${nextPage.key}`)}</span>
              <ChevronRight className="w-4 h-4" />
            </NavLink>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
};

export default PageNavigation;
